// https://github.com/JohnCampionJr/vite-plugin-vue-layouts/issues/114#issuecomment-1665922024
import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordNormalized } from 'vue-router'
import i18n from '@/plugins/i18n'
import routes from '~pages'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useStudentStore } from '@/stores/student'
import { checkPermissions } from '@/composable/useCheckPermissions'
import type { UserProfilePermissionLevel } from '@/enums/UserProfilePermissionLevel'

// Declare types for a route meta
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth: boolean
  }
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    ...setupLayouts(routes),
  ],
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

const updateDocumentTitle = (to: RouteLocationNormalized) => {
  const appStore = useAppStore()
  let title = (to.meta.title) ?? 'page_titles.my_future_choice'
  title = i18n.global.t(title)
  document.title = `${appStore.config.APP_NAME} - ${title}`
}

const ensureAuthClaims = async (authStore: ReturnType<typeof useAuthStore>): Promise<void> => {
  if (authStore.hasValidAccessToken) {
    authStore.restoreJwtClaims()

    return
  }

  if (!authStore.hasRefreshToken) return

  authStore.restoreJwtClaimsFromToken()

  if (authStore.hasValidAccessToken) return

  try {
    await authStore.refreshToken()
    authStore.restoreJwtClaims()
  }
  catch {
    // Refresh failed - getLoggedInUser will 401 and trigger its own refresh, or we'll redirect to login
  }
}

const loadUserData = async (): Promise<boolean> => {
  const authStore = useAuthStore()

  await ensureAuthClaims(authStore)

  try {
    await authStore.getLoggedInUser()

    return authStore.authorised
  }
  catch (error) {
    // Check if this was a shadow org mismatch after token refresh
    if (error instanceof Error && error.message === 'SHADOW_ORG_MISMATCH' && authStore.hasValidAccessToken) {
      // Token was refreshed but org changed (multi-tab shadowing)
      // Try loading user data again with fresh JWT claims
      authStore.restoreJwtClaims()
      try {
        await authStore.getLoggedInUser()

        return authStore.authorised
      }
      catch (retryError) {
        // Second attempt failed - fall through to normal error handling
      }
    }

    // If call failed, check if we still have refresh token
    // If refresh token is gone, auth failed
    if (!authStore.hasRefreshToken) {
      return false
    }

    // Still have refresh token - might have been a transient error
    // Check if user became authorized anyway
    return authStore.authorised
  }
}

const handleAuth = async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()

  // If we have a valid token and user is already loaded, we're good
  if (authStore.hasValidAccessToken && authStore.authorised) {
    return null
  }

  // If we have tokens (either valid access token or refresh token), try to load user data
  if (authStore.hasValidAccessToken || authStore.hasRefreshToken) {
    const isAuthorised = await loadUserData()

    if (isAuthorised) {
      return null
    }

    // User data loaded but not authorized
    if (authStore.hasRefreshToken) {
      return { name: 'auth.unauthorised' }
    }

    // No refresh token means auth failed
    window.sessionStorage.setItem('requested_page', to.fullPath)

    return { name: 'auth.login' }
  }

  // No tokens at all - need to login
  window.sessionStorage.setItem('requested_page', to.fullPath)

  return { name: 'auth.login' }
}

const handleInterviewingMode = async (record: RouteRecordNormalized) => {
  const authStore = useAuthStore()

  if (record.meta.isForInterviewer) {
    if (!authStore.is_interviewer) {
      return { name: 'auth.unauthorised' }
    }

    authStore.setSelectedInterviewing(true)
  } else {
    if (null === authStore.selected_organisation_id && record.name != 'auth.user-profile-selector') {
      return { name: 'auth.user-profile-selector' }
    }
    if (authStore.selected_interviewing) {
      return { name: 'interviewer-days.list' }
    }
    authStore.setSelectedInterviewing(false)
  }
}

router.beforeEach(async (to, from) => {
  const authStore = useAuthStore()

  updateDocumentTitle(to)

  const routerRecord = to.matched.find(record => record.meta.requiresAuth)
  if (!routerRecord) return // No need to check further if the route doesn't require auth

  // Handle authentication - check for valid token or refresh token
  // If we have a refresh token but no valid access token, handleAuth will trigger
  // an API call that the axios interceptor will catch and refresh automatically
  if (!authStore.hasValidAccessToken || !authStore.authorised) {
    const result = await handleAuth(to)

    if (result) return result // If handleAuth returns a result, use it
  }

  const result = await handleInterviewingMode(routerRecord)

  if (result) return result // If returns a result, use it

  // Check if the user is authorized
  // or if the route is restricted for interviewers
  // and permissions
  if (
    !authStore.authorised
    || (authStore.selected_interviewing && routerRecord.meta.restrictedForInterviewer)
    || (routerRecord.meta.permissionLevel && !checkPermissions(routerRecord.meta.permissionLevel as UserProfilePermissionLevel))
  ) {
    return { name: 'auth.unauthorised' }
  }

  // Clear search filter when navigating to overview from student detail
  if (to.name === 'students.overview' && from.name === 'organisation.student-detail') {
    const studentStore = useStudentStore()
    if (studentStore.filters.search) {
      studentStore.filters.search = null
    }
  }
})

export default router
